import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExerciseCoachComposer from './ExerciseCoachComposer';
import ExerciseCoachMessageAttachments from './ExerciseCoachMessageAttachments';
import CoachAvatar from '../CoachAvatar';
import { DEFAULT_EXERCISE_CHAT_EXPANDED, exerciseCoachEntryKey, exerciseCoachHistoryKey, historyScrollTop, latestExerciseCoachEntry, mergeExerciseCoachConversations, uniqueExerciseCoachEntries, visibleExerciseCoachEntries } from './exerciseCoachConversation';

const EMPTY_EXERCISE_NAMES = Object.freeze({});

// Typewriter effect hook for RPG-style text reveal (mirrors WorkoutExercise.js)
const useTypewriter = (text, speed = 80) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const prevTextRef = useRef('');

  useEffect(() => {
    const str = (typeof text === 'string') ? text : (text != null ? String(text) : '');
    if (!str || str === prevTextRef.current) return;

    prevTextRef.current = str;
    const words = str.split(' ');
    let currentIndex = 0;

    setDisplayedText('');
    setIsTyping(true);

    const typeNextWord = () => {
      if (currentIndex < words.length) {
        const newText = words.slice(0, currentIndex + 1).join(' ');
        setDisplayedText(newText);
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(intervalId);
      }
    };

    typeNextWord();
    const intervalId = setInterval(typeNextWord, speed);

    return () => clearInterval(intervalId);
  }, [text, speed]);

  return { displayedText: displayedText || text || '', isTyping };
};

/**
 * Self-contained AI coach chat for a single workout_exercise. Identical UX
 * to the chat block originally inlined in WorkoutExercise.js, so it can be
 * reused inside supersets (one chat per selected exercise).
 */
function ExerciseCoachChat({
  workout,
  workoutExerciseId,
  workoutExerciseIds,
  exerciseNamesById = EMPTY_EXERCISE_NAMES,
  backendService,
  refreshWorkout,
  recommendation,
  isLoadingRecommendation = false,
  isUpdatingRecommendation = false,
  onSubmitMessage,
  onAnswerQuestion,
  coachImageUrl = null,
  coachEmoji = '🥋',
}) {
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const [conversationLog, setConversationLog] = useState([]);
  const [showFullChat, setShowFullChat] = useState(DEFAULT_EXERCISE_CHAT_EXPANDED);
  const conversationExerciseIds = useMemo(() => (
    Array.isArray(workoutExerciseIds) && workoutExerciseIds.length
      ? workoutExerciseIds.map(String)
      : [String(workoutExerciseId)]
  ), [workoutExerciseIds, workoutExerciseId]);
  const conversationScopeKey = conversationExerciseIds.join('|');

  const chatHistoryRef = useRef(null);
  const uniqueConversationLog = uniqueExerciseCoachEntries(conversationLog);
  const historyKey = exerciseCoachHistoryKey({
    workoutId: workout?._id,
    exerciseId: workoutExerciseId,
    conversationLength: uniqueConversationLog.length,
  });

  const pendingQuestion = recommendation?.question;
  const questionType = recommendation?.question_type;
  const questionChoices = recommendation?.question_choices;

  // Fetch conversation log when exercise changes or recommendation updates
  useEffect(() => {
    let active = true;
    if (workout?._id && conversationExerciseIds.length) {
      Promise.all(conversationExerciseIds.map(async (exerciseId) => ({
        exerciseId,
        exerciseName: exerciseNamesById[exerciseId],
        conversationLog: (await backendService.getExerciseConversation(workout._id, exerciseId)).conversation_log || [],
      })))
        .then(data => { if (active) setConversationLog(mergeExerciseCoachConversations(data)); })
        .catch(() => { if (active) setConversationLog([]); });
    }
    return () => { active = false; };
  }, [workout?._id, conversationScopeKey, conversationExerciseIds, exerciseNamesById, backendService, recommendation?.summary]);

  // Scroll the nested history itself, not the page, to the newest entry.
  useEffect(() => {
    if (chatHistoryRef.current && uniqueConversationLog.length > 0 && showFullChat) {
      let settleTimer;
      let transitionTimer;
      requestAnimationFrame(() => {
        const scrollHistoryToBottom = () => {
          const history = chatHistoryRef.current;
          if (history) history.scrollTop = historyScrollTop(history);
        };
        scrollHistoryToBottom();
        requestAnimationFrame(scrollHistoryToBottom);
        transitionTimer = setInterval(scrollHistoryToBottom, 16);
        settleTimer = setTimeout(() => {
          clearInterval(transitionTimer);
          scrollHistoryToBottom();
        }, 260);
      });
      return () => {
        clearTimeout(settleTimer);
        clearInterval(transitionTimer);
      };
    }
    return undefined;
  }, [historyKey, showFullChat, uniqueConversationLog.length]);


  const { isTyping } = useTypewriter(recommendation?.summary, 30);
  const latestCoachEntry = latestExerciseCoachEntry(uniqueConversationLog);
  const visibleCoachEntries = visibleExerciseCoachEntries({
    conversation: uniqueConversationLog,
    expanded: showFullChat,
    fallbackMessage: recommendation?.summary,
  });

  const refreshConversationLog = async () => {
    try {
      const data = await Promise.all(conversationExerciseIds.map(async (exerciseId) => ({
        exerciseId,
        exerciseName: exerciseNamesById[exerciseId],
        conversationLog: (await backendService.getExerciseConversation(workout._id, exerciseId)).conversation_log || [],
      })));
      setConversationLog(mergeExerciseCoachConversations(data));
    } catch (err) {
      console.error('Failed to refresh conversation log:', err);
    }
  };

  const handleAnswerQuestion = async (answer) => {
    if (!onAnswerQuestion || isSubmittingMessage) return;
    setIsSubmittingMessage(true);
    try {
      await onAnswerQuestion(workoutExerciseId, answer);
      await refreshConversationLog();
    } catch (error) {
      console.error('Error answering question:', error);
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const handleSubmitUserMessage = async (message, attachments = []) => {
    if (!onSubmitMessage || (!message.trim() && attachments.length === 0) || isSubmittingMessage) return;
    setIsSubmittingMessage(true);
    try {
      await onSubmitMessage(workoutExerciseId, message.trim(), attachments);
      await refreshConversationLog();
    } catch (error) {
      console.error('Error submitting message:', error);
      if (error?.messageSaved) await refreshConversationLog();
      throw error;
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const handleComposerSubmit = async (message, attachments) => {
    if (pendingQuestion && !isUpdatingRecommendation && attachments.length === 0 && message.trim()) {
      await handleAnswerQuestion(message.trim());
      return;
    }
    await handleSubmitUserMessage(message, attachments);
  };

  if (!onSubmitMessage && !recommendation && !isLoadingRecommendation) {
    return null;
  }

  return (
    <div className={`exercise-coach-chat${showFullChat ? ' chat-expanded' : ''}`}>
      {conversationLog.length > 0 && (
        <button
          className="chat-history-toggle"
          onClick={() => setShowFullChat(!showFullChat)}
        >
          {showFullChat ? 'Hide previous messages' : `Show previous messages (${conversationLog.length})`}
        </button>
      )}

      {(visibleCoachEntries.length > 0 || isLoadingRecommendation || isUpdatingRecommendation) && (
        <div className={`chat-history${showFullChat ? ' chat-history-expanded' : ' chat-history-collapsed'}`} ref={chatHistoryRef}>
          {visibleCoachEntries.map((entry, idx) => (
            <div key={exerciseCoachEntryKey(entry, idx)} className={`chat-message chat-message-${entry.type}${entry === latestCoachEntry ? ' chat-message-latest-coach' : ''}`}>
              {conversationExerciseIds.length > 1 && entry._conversationExerciseName && (
                <span className="exercise-coach-entry-scope">{entry._conversationExerciseName}</span>
              )}
              {(entry.type === 'ai_recommendation' || entry.type === 'ai_message') && (
                <>
                  <CoachAvatar imageUrl={coachImageUrl} emoji={coachEmoji} identitySettled alt="Coach" className="chat-icon coach-chat-image" />
                  <span className="chat-content">{entry.data?.message || entry.content}</span>
                </>
              )}
              {entry.type === 'ai_question' && (
                <>
                  <CoachAvatar imageUrl={coachImageUrl} emoji={coachEmoji} identitySettled alt="Coach" className="chat-icon coach-chat-image" />
                  <span className="chat-content">{entry.data?.question || entry.data?.message || entry.content}</span>
                </>
              )}
              {(entry.type === 'user_response' || entry.type === 'user_message') && (
                <>
                  <span className="chat-content user">{entry.data?.message || (entry.data?.attachments?.length ? 'Photo attached' : entry.content)}</span>
                  <ExerciseCoachMessageAttachments attachments={entry.data?.attachments} backendService={backendService} />
                  <span className="chat-icon">👤</span>
                </>
              )}
              {entry.type === 'set_complete' && (
                <span className="chat-content set-complete">
                  ✓ Set {(entry.data?.set_index ?? 0) + 1}: {entry.data?.weight > 0 ? `${entry.data.weight} × ` : ''}{entry.data?.repetitions > 0 ? `${entry.data.repetitions} reps` : ''}{entry.data?.duration > 0 ? `${Math.round(entry.data.duration / 1000)}s` : ''}
                </span>
              )}
            </div>
          ))}

        </div>
      )}

      {pendingQuestion && !isTyping && !isUpdatingRecommendation && (
        <div className="chat-question-text">
          <CoachAvatar imageUrl={coachImageUrl} emoji={coachEmoji} identitySettled alt="Coach" className="chat-icon coach-chat-image" />
          <span className="chat-content">{pendingQuestion}</span>
        </div>
      )}

      {onSubmitMessage && workout.status !== 'complete' && (
        <div className="chat-input-row">
          {pendingQuestion && !isTyping && !isUpdatingRecommendation && questionType !== 'open' && (
            <div className="chat-quick-answers">
              {questionType === 'yes_no' && (
                <>
                  <button
                    className="quick-answer-btn"
                    onClick={() => handleAnswerQuestion('Yes')}
                    disabled={isSubmittingMessage}
                  >
                    Yes
                  </button>
                  <button
                    className="quick-answer-btn"
                    onClick={() => handleAnswerQuestion('No')}
                    disabled={isSubmittingMessage}
                  >
                    No
                  </button>
                </>
              )}
              {questionType === 'choice' && questionChoices && questionChoices.map((choice, idx) => (
                <button
                  key={idx}
                  className="quick-answer-btn"
                  onClick={() => handleAnswerQuestion(choice)}
                  disabled={isSubmittingMessage}
                >
                  {choice}
                </button>
              ))}
              <button
                className="quick-answer-btn skip"
                onClick={() => handleAnswerQuestion('[skipped]')}
                disabled={isSubmittingMessage}
              >
                Skip
              </button>
            </div>
          )}
          <ExerciseCoachComposer
            onSubmit={handleComposerSubmit}
            disabled={isSubmittingMessage}
            resetKey={pendingQuestion || ''}
            placeholder={pendingQuestion && !isUpdatingRecommendation ? 'Other, or ask something else...' : 'Ask coach...'}
          />
        </div>
      )}
    </div>
  );
}

export default ExerciseCoachChat;
